/**
 * EventStream — Compact event feed showing the last 10 system events.
 *
 * Displays a collapsible, scrollable list of platform events with color-coded
 * type indicators and relative timestamps. Uses mock data until an API is
 * available.
 */
import { useState } from 'react';
import { makeStyles, tokens, Caption1, Body2, Button } from '@fluentui/react-components';
import { ChevronDown24Regular, ChevronUp24Regular } from '@fluentui/react-icons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EventType =
  | 'pr-blocked'
  | 'policy-evaluated'
  | 'evidence-generated'
  | 'collector-event'
  | 'workflow-task';

interface SystemEvent {
  id: string;
  type: EventType;
  description: string;
  /** ISO-8601 timestamp */
  timestamp: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EVENT_COLORS: Record<EventType, string> = {
  'pr-blocked': '#E85252',
  'policy-evaluated': '#8852E0',
  'evidence-generated': '#0078D4',
  'collector-event': '#22C55E',
  'workflow-task': '#F59E0B',
};

const EVENT_LABELS: Record<EventType, string> = {
  'pr-blocked': 'PR Blocked',
  'policy-evaluated': 'Policy Evaluated',
  'evidence-generated': 'Evidence Generated',
  'collector-event': 'Collector Event',
  'workflow-task': 'Workflow Task',
};

// ---------------------------------------------------------------------------
// Mock data — 10 realistic events
// ---------------------------------------------------------------------------

function minutesAgo(n: number): string {
  return new Date(Date.now() - n * 60_000).toISOString();
}

const MOCK_EVENTS: SystemEvent[] = [
  {
    id: 'evt-001',
    type: 'pr-blocked',
    description: 'PR #482 blocked — critical secret detected in config/db.yml',
    timestamp: minutesAgo(2),
  },
  {
    id: 'evt-002',
    type: 'policy-evaluated',
    description: 'SOC 2 Type II policy evaluation completed for payment-service',
    timestamp: minutesAgo(5),
  },
  {
    id: 'evt-003',
    type: 'evidence-generated',
    description: 'Evidence artifact generated for ISO 27001 A.12.6.1 control',
    timestamp: minutesAgo(8),
  },
  {
    id: 'evt-004',
    type: 'collector-event',
    description: 'AWS SecurityHub collector synced 34 findings from us-east-1',
    timestamp: minutesAgo(12),
  },
  {
    id: 'evt-005',
    type: 'workflow-task',
    description: 'Remediation task assigned to platform-team for CVE-2026-1847',
    timestamp: minutesAgo(18),
  },
  {
    id: 'evt-006',
    type: 'pr-blocked',
    description: 'PR #479 blocked — dependency vulnerability (high) in lodash@4.17.20',
    timestamp: minutesAgo(25),
  },
  {
    id: 'evt-007',
    type: 'evidence-generated',
    description: 'Compliance evidence snapshot created for Q1 2026 audit cycle',
    timestamp: minutesAgo(33),
  },
  {
    id: 'evt-008',
    type: 'collector-event',
    description: 'Azure Defender collector ingested 12 new alerts from subscription prod-01',
    timestamp: minutesAgo(41),
  },
  {
    id: 'evt-009',
    type: 'policy-evaluated',
    description: 'NIST 800-53 policy gate passed for infrastructure-core repository',
    timestamp: minutesAgo(55),
  },
  {
    id: 'evt-010',
    type: 'workflow-task',
    description: 'Attestation review task created for access-control policy renewal',
    timestamp: minutesAgo(68),
  },
];

// ---------------------------------------------------------------------------
// Relative time formatter
// ---------------------------------------------------------------------------

function relativeTime(isoTimestamp: string): string {
  const diffMs = Date.now() - new Date(isoTimestamp).getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerLabel: {
    fontWeight: 600 as unknown as string,
    color: tokens.colorNeutralForeground1,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground5,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '300px',
    overflowY: 'auto',
    gap: tokens.spacingVerticalXS,
  },
  eventRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    animationName: 'eventStreamFadeIn',
    animationDuration: '0.3s',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'both' as unknown as string,
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: '6px',
  },
  eventContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flex: '1 1 auto',
  },
  eventDescription: {
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase200,
  },
  eventMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  eventType: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
  },
  eventTime: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase100,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EventStream() {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={styles.root}>
      {/* Inline keyframes — injected once */}
      <style>{`
        @keyframes eventStreamFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Body2 className={styles.headerLabel}>Event Stream</Body2>
          <span className={styles.badge}>{MOCK_EVENTS.length}</span>
        </div>
        <Button
          appearance="subtle"
          size="small"
          icon={expanded ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={expanded ? 'Collapse event stream' : 'Expand event stream'}
        />
      </div>

      {/* Event list */}
      {expanded && (
        <div className={styles.list} role="log" aria-label="System event stream">
          {MOCK_EVENTS.map((evt, index) => (
            <div
              key={evt.id}
              className={styles.eventRow}
              style={{ animationDelay: `${index * 40}ms` }}
            >
              <span
                className={styles.dot}
                style={{ backgroundColor: EVENT_COLORS[evt.type] }}
                aria-hidden="true"
              />
              <div className={styles.eventContent}>
                <Body2 className={styles.eventDescription}>{evt.description}</Body2>
                <div className={styles.eventMeta}>
                  <Caption1
                    className={styles.eventType}
                    style={{ color: EVENT_COLORS[evt.type] }}
                  >
                    {EVENT_LABELS[evt.type]}
                  </Caption1>
                  <Caption1 className={styles.eventTime}>
                    {relativeTime(evt.timestamp)}
                  </Caption1>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
